import {
  collectAssetIds,
  type ArticleDoc,
  type AssetMap,
  type AssetRecord,
} from '@archetelos/editor';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { supabase } from '@/lib/supabase';

const ASSET_FIELDS = 'id, public_url, alt_text, caption, credit, width, height';

/** A library grid row — an asset plus how many essays reference it. */
export interface AssetListItem {
  id: string;
  publicUrl: string;
  altText: string;
  caption: string | null;
  credit: string | null;
  createdAt: string;
  usageCount: number;
}

/** An essay that references an asset (the "used in" list). */
export interface UsageRef {
  id: string;
  title: string;
  slug: string;
  type: string;
}

interface AssetRow {
  id: string;
  public_url: string;
  alt_text: string;
  caption: string | null;
  credit: string | null;
  width: number | null;
  height: number | null;
}

function toAssetRecord(r: AssetRow): AssetRecord {
  return {
    id: r.id,
    publicUrl: r.public_url,
    altText: r.alt_text,
    caption: r.caption,
    credit: r.credit,
    width: r.width,
    height: r.height,
  };
}

export const assetQueries = {
  // Resolve exactly the assets a doc references. Sorted key so [a,b] and [b,a]
  // share the cache.
  byIds: (ids: string[]) =>
    queryOptions({
      queryKey: ['assets', 'by-ids', [...ids].sort()] as const,
      queryFn: async (): Promise<AssetMap> => {
        if (ids.length === 0) return {};
        const { data, error } = await supabase.from('assets').select(ASSET_FIELDS).in('id', ids);
        if (error) throw error;
        const map: AssetMap = {};
        for (const r of (data ?? []) as AssetRow[]) map[r.id] = toAssetRecord(r);
        return map;
      },
      enabled: ids.length > 0,
    }),

  // The library grid + the composer's reuse tab. `all` is an ADMIN query filter,
  // not a security boundary — assets are anon-readable by design (the SSG build),
  // so scoping to own vs. all is a filter, not RLS. usageCount from the join.
  list: (opts: { userId: string | undefined; all: boolean }) =>
    queryOptions({
      queryKey: ['assets', 'list', opts.all ? 'all' : (opts.userId ?? 'none')] as const,
      queryFn: async (): Promise<AssetListItem[]> => {
        let q = supabase
          .from('assets')
          .select('id, public_url, alt_text, caption, credit, created_at, content_assets(count)')
          .order('created_at', { ascending: false });
        if (!opts.all && opts.userId) q = q.eq('uploaded_by', opts.userId);
        const { data, error } = await q;
        if (error) throw error;
        return (data ?? []).map((r) => ({
          id: r.id,
          publicUrl: r.public_url,
          altText: r.alt_text,
          caption: r.caption,
          credit: r.credit,
          createdAt: r.created_at,
          usageCount: (r.content_assets as { count: number }[] | null)?.[0]?.count ?? 0,
        }));
      },
      enabled: opts.all || Boolean(opts.userId),
    }),

  // "Used in": the essays referencing an asset, read from the reverse index.
  // content's own RLS scopes what titles a caller can see (self-limiting join).
  usage: (id: string | undefined) =>
    queryOptions({
      queryKey: ['assets', 'usage', id] as const,
      queryFn: async (): Promise<UsageRef[]> => {
        if (!id) return [];
        const { data, error } = await supabase
          .from('content_assets')
          .select('content:content_id (id, title, slug, type)')
          .eq('asset_id', id);
        if (error) throw error;
        return (data ?? [])
          .map((r) => r.content as UsageRef | null)
          .filter((c): c is UsageRef => c !== null);
      },
      enabled: Boolean(id),
    }),

  // One lookup per distinct assetId, shared across NodeView instances via the cache.
  byId: (id: string | undefined) =>
    queryOptions({
      queryKey: ['assets', 'by-id', id] as const,
      queryFn: async (): Promise<AssetRecord | null> => {
        if (!id) return null;
        const { data, error } = await supabase
          .from('assets')
          .select(ASSET_FIELDS)
          .eq('id', id)
          .maybeSingle();
        if (error) throw error;
        return data ? toAssetRecord(data as AssetRow) : null;
      },
      enabled: Boolean(id),
    }),
};

/**
 * The shared resolver hook for every app surface that renders a body (reader,
 * creator preview, admin drawer, composer preview) — collectAssetIds + fetch, so
 * the four sites don't each re-implement it (Spec 39 confirm 1). Returns {} while
 * loading; renderDocument then drops figures until the map arrives.
 */
export function useAssetMap(doc: ArticleDoc | null | undefined): AssetMap {
  const ids = useMemo(() => collectAssetIds(doc), [doc]);
  const { data } = useQuery(assetQueries.byIds(ids));
  return data ?? {};
}
